/**
 * Video Sync Module
 * 
 * Synchronizes playback state between light and dark video variants
 * when the theme is switched. This ensures a seamless user experience
 * by preserving playback position, play/pause state, volume, and mute status.
 * 
 * Usage:
 *   import { initVideoSync } from './components/video-sync.js'
 *   initVideoSync()
 */

/**
 * Capture the current state from a video element
 * @param {HTMLVideoElement} video - The video element to capture state from
 * @returns {Object} State object containing playback information
 */
function captureVideoState(video) {
	if (!video) return null

	return {
		currentTime: video.currentTime,
		paused: video.paused,
		volume: video.volume,
		muted: video.muted,
		playbackRate: video.playbackRate,
		ended: video.ended
	}
}

/**
 * Apply a captured state to a video element
 * @param {HTMLVideoElement} video - The video element to apply state to
 * @param {Object} state - The state object to apply
 */
function applyVideoState(video, state) {
	if (!video || !state) return

	// Apply basic properties
	video.currentTime = state.currentTime
	video.volume = state.volume
	video.muted = state.muted
	video.playbackRate = state.playbackRate

	// Resume playback if the source video was playing
	if (!state.paused && !state.ended && video.paused) {
		// Use a promise to handle potential autoplay issues
		video.play().catch(err => {
			console.warn('Video autoplay prevented by browser:', err)
			// Browsers may block autoplay, which is expected behavior
		})
	} else if (state.paused && !video.paused) {
		video.pause()
	}
}

/**
 * Sync state between paired light and dark videos
 * @param {string} currentTheme - The current active theme ('light' or 'dark')
 */
function syncVideoPairs(currentTheme) {
	// Find all video pairs on the page
	const lightVideos = document.querySelectorAll('video[data-video-pair="light"]')
	const darkVideos = document.querySelectorAll('video[data-video-pair="dark"]')

	// Ensure we have matching pairs
	if (lightVideos.length === 0 || darkVideos.length === 0) {
		return
	}

	if (currentTheme === 'dark') {
		// Switching to dark: capture light video states, apply to dark videos
		lightVideos.forEach((lightVideo, index) => {
			const darkVideo = darkVideos[index]
			if (darkVideo) {
				const state = captureVideoState(lightVideo)
				applyVideoState(darkVideo, state)
			}
		})
	} else if (currentTheme === 'light') {
		// Switching to light: capture dark video states, apply to light videos
		darkVideos.forEach((darkVideo, index) => {
			const lightVideo = lightVideos[index]
			if (lightVideo) {
				const state = captureVideoState(darkVideo)
				applyVideoState(lightVideo, state)
			}
		})
	}
}

/**
 * Initialize video synchronization by observing theme changes
 * Sets up a MutationObserver on the document root to watch for data-theme changes
 */
export function initVideoSync() {
	// Create an observer to watch for theme changes on the html element
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.attributeName === 'data-theme') {
				const currentTheme = document.documentElement.getAttribute('data-theme')
				
				// Only sync if we have a valid theme
				if (currentTheme === 'light' || currentTheme === 'dark') {
					// Small delay to ensure CSS transitions have started
					requestAnimationFrame(() => {
						syncVideoPairs(currentTheme)
					})
				}
			}
		})
	})

	// Observe the html element for attribute changes
	observer.observe(document.documentElement, {
		attributes: true,
		attributeFilter: ['data-theme']
	})

	// Log initialization for debugging
	if (import.meta.env.DEV) {
		console.log('Video sync initialized')
	}
}
